import { Title } from '../../../../components/Typography/Title';
import { Toolbar } from '../../../../components/Toolbar';
import { Table } from '../../../../components/Table';
import { Container } from './styles';
import {
	Button,
	Dropdown,
	Form,
	Icon,
	Label,
	PaginationProps,
	Popup,
} from 'semantic-ui-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { FilterGroup } from '../../../../components/Form/FilterGroup';
import { Controller, useForm } from 'react-hook-form';
import { useDeleteCapacityLevel } from '../../hooks/useDeleteCapacityLevel';
import { useFindCapacityLevels } from '../../hooks/useFindCapacityLevels';
import { FilterListCapacityLevels } from '../../types/capacityLevel.type';
import { NoResultsTableInformation } from '../../../../components/TableNoDataFound';
import { useTranslation } from 'react-i18next';
import { TableHealderProps } from '../../../../types/table.types';
import { ModalConfirm } from '../../../../components/ModalConfirm';

const tableHeader: TableHealderProps[] = [
	{ name: 'page.capacityLevels.capacityLevel' },
	{ name: 'page.capacityLevels.active', width: 2, textAlign: 'center' },
];

export function ListCapacityLevels() {
	const [openModal, setOpenModal] = useState<boolean>(false);
	const [item, setItem] = useState<number>();

	const { t } = useTranslation();
	const { control, handleSubmit, reset } = useForm<FilterListCapacityLevels>({
		defaultValues: {
			active: true,
		},
	});
	const [page, setPage] = useState(1);
	const [filter, setFilter] = useState<FilterListCapacityLevels>({
		active: true,
	});

	const navigate = useNavigate();
	const location = useLocation();

	const { listCapacityLevels } = useFindCapacityLevels({ page, filter });
	const { deleteCapacityLevel } = useDeleteCapacityLevel();

	const onSubmitFilter = (data: FilterListCapacityLevels) => {
		setFilter(data);
		setPage(1);
	};

	const handleResetFilter = () => {
		reset({
			name: '',
			active: true,
		});
		setFilter({
			name: undefined,
			active: true,
		});
	};

	const handlePageChange = (
		_: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
		data: PaginationProps
	) => {
		setPage(Number(data.activePage));
	};

	return (
		<Container>
			<Title>{t('title.capacityLevels')}</Title>

			<Toolbar
				handleAdd={() => navigate(`${location.pathname}/create`)}
				filter={{
					content: (
						<>
							<FilterGroup>
								<Controller
									control={control}
									name="name"
									render={({ field: { value, onChange } }) => (
										<Form.Input
											label={t('page.capacityLevels.capacityLevel')}
											value={value}
											onChange={onChange}
										/>
									)}
								/>
							</FilterGroup>

							<FilterGroup>
								<Controller
									control={control}
									name="active"
									render={({ field: { value, onChange } }) => (
										<Form.Radio
											toggle
											label={t('page.capacityLevels.active')}
											checked={value}
											onChange={(_, data) => onChange(data.checked)}
										/>
									)}
								/>
							</FilterGroup>
						</>
					),
					onApply: handleSubmit(onSubmitFilter),
					onClear: handleResetFilter,
				}}
			></Toolbar>

			<span>
				<Table>
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell content="ID" isIdColumn />
							{tableHeader.map(({ name, width, textAlign }) => (
								<Table.HeaderCell
									key={name}
									width={width}
									textAlign={textAlign}
								>
									{t(name as any)}
								</Table.HeaderCell>
							))}

							<Table.HeaderCell isActionColumn>
								<Icon name="cog" size="large" />
							</Table.HeaderCell>
						</Table.Row>
					</Table.Header>

					<Table.Body>
						{listCapacityLevels?._embedded?.capacityLevels?.map(
							({ id, active, name }, index) => (
								<Table.Row key={id}>
									<Table.Cell content={index + 1} />
									<Table.Cell content={name} />
									<Table.Cell width={2} textAlign="center">
										<Label
											color={active ? 'green' : 'red'}
											content={active ? 'Active' : 'Deactivate'}
										/>
									</Table.Cell>
									<Table.Cell isActionColumn>
										<Dropdown
											pointing="right"
											trigger={<Icon name="ellipsis vertical" size="large" />}
											icon={null}
										>
											<Dropdown.Menu>
												<span className="actionColumn__buttonGroup">
													<Popup
														content={t('tooltip.edit')}
														trigger={
															<Button
																icon="pencil"
																size="mini"
																color="yellow"
																onClick={() =>
																	navigate(`${location.pathname}/update/${id}`)
																}
															/>
														}
													/>

													<Popup
														content={t('tooltip.delete')}
														trigger={
															<Button
																icon="trash"
																size="mini"
																color="red"
																onClick={() => {
																	setItem(id);
																	setOpenModal(true);
																}}
															/>
														}
													/>
												</span>
											</Dropdown.Menu>
										</Dropdown>
									</Table.Cell>
								</Table.Row>
							)
						)}
					</Table.Body>
				</Table>

				{listCapacityLevels?._embedded?.capacityLevels?.length ? (
					<Table.Pagination
						page={page}
						activePage={page}
						totalPages={listCapacityLevels?.page.totalPages ?? 0}
						boundaryRange={1}
						siblingRange={0}
						onPageChange={handlePageChange}
						size="mini"
						firstItem={null}
						lastItem={null}
						secondary
					/>
				) : (
					<NoResultsTableInformation
						icon="dont"
						title={t('message.noDataFound.title')}
						subTitle={t('message.noDataFound.subTitle')}
					/>
				)}
			</span>

			<Outlet />

			<ModalConfirm
				open={openModal}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				title={t('component.modal.header.confirmation')}
				description={t('component.modal.content.AreYouSureYouWantToDelete')}
				type="remove"
				size="mini"
				onConfirm={() => {
					item && deleteCapacityLevel.mutateAsync(item);
					setOpenModal(false);
				}}
				onCancel={() => setOpenModal(false)}
			/>
		</Container>
	);
}
